// drop-meshes.ts — pick a small 3D prop for a dropped item.
//
// Hand-dropped items (and any ground loot) render as a little 3D object on the
// ground, matching how creature kills drop loot (a tinted nature prop — meat is a
// red mushroom, leather a brown pebble, etc.). Every item resolves to SOME mesh:
//   1. a per-material mapping for the raw materials / foods (reuses the same look
//      the creature-drop ResourceNodes use), then
//   2. a generic prop per equipment slot for gear that has no 3D model — a tusk
//      for weapons, a hide for armour, a flat stone for off-hands, a mushroom for
//      consumables (approximate but native 3D, the same spirit as the meat=mushroom
//      creature drops). Aviv 2026-06-26.
import { ITEMS, type NatureKey } from "@tribez/shared";
import type { DropPropKey } from "../assets";

export interface DropMeshSpec {
  source?: "nature" | "prop"; // which bundle to clone from (default "nature")
  key: NatureKey | DropPropKey;
  height: number;       // TARGET world height in metres (the prop is scaled to this,
                        //   measured from its bbox — so sizing is consistent across
                        //   meshes of different native sizes)
  tint?: number;        // luminance-preserving recolour (toToon tintColor)
  emissive?: number;    // faint self-glow hue so the drop is findable on the ground
}

// Explicit material / food look — keyed by item id. Mirrors resources.ts drop
// kinds so a dropped material matches the one a creature kill produces.
const MATERIAL: Record<string, DropMeshSpec> = {
  Stone:        { key: "exRockM1A",    height: 0.75, tint: 0xc4ccda },
  IronOre:      { key: "exRockS1A",    height: 0.6,  tint: 0x8f97a2, emissive: 0x202428 },
  Lumber:       { key: "exLog1A",      height: 0.55, tint: 0x7a5230 },
  InfusedLumber:{ key: "exLog1A",      height: 0.55, tint: 0x6a8a44, emissive: 0x182810 },
  Leather:      { key: "pebbleRound3", height: 0.5,  tint: 0x6b3d24 },
  BearPelt:     { key: "pebbleRound3", height: 0.6,  tint: 0x4a2818 },
  Wool:         { key: "bushCommon",   height: 0.6,  tint: 0xe8e6e0 },
  Antlers:      { key: "exTusk1A",     height: 0.8,  tint: 0xd6c8a0 },
  WhiteBearFang:{ key: "exTusk1A",     height: 0.5,  tint: 0xf0e8d0 },
  SkeletalFinger:{ key: "exBone1A",    height: 0.6,  tint: 0xe8d8b8 },
  Berry:        { key: "bushCommon",   height: 0.5,  tint: 0x9a2f4a, emissive: 0x300814 },
  HandfulofBerries:{ key: "bushCommon",height: 0.55, tint: 0x9a2f4a, emissive: 0x300814 },
  Sageberry:    { key: "bushCommon",   height: 0.5,  tint: 0x8040a0, emissive: 0x201030 },
  Herb:         { key: "exFern1A",     height: 0.6,  tint: 0x5fa040, emissive: 0x162810 },
  SpicyHerbs:   { key: "exFern1A",     height: 0.6,  tint: 0xd05030, emissive: 0x301810 },
  Wolfsbane:    { key: "exFern1A",     height: 0.6,  tint: 0x7a6fb0, emissive: 0x1a1830 },
  LambentSunflower:{ key: "exFern1A",  height: 0.6,  tint: 0xe0c040, emissive: 0x303010 },
  Shadowstone:  { key: "rockMedium3",  height: 0.6,  tint: 0x6a5f88, emissive: 0x201038 },
  RadiantGem:   { key: "rockMedium3",  height: 0.55, tint: 0xffe080, emissive: 0xc0a030 },
  Shadowshard:  { key: "rockMedium3",  height: 0.55, tint: 0x6a5f88, emissive: 0x201038 },
  AncientArtifactFragment:{ key: "rockMedium3", height: 0.6, tint: 0xc0a060, emissive: 0x302010 },
};
// Raw meats read as the EXPLORER roasted-meat prop; fish stay a tinted mushroom.
const MEAT_RE = /^(Raw|.*Meat$|.*Lamb$|.*Fox$|.*Pheasant$)/;
const MEAT: DropMeshSpec = { source: "prop", key: "roastedMeat", height: 0.4, tint: 0x9a4a2e };
const FISH_RE = /(fish|Fish|trout|Trout|Guppy|Danio|Eel|Gourami|Sunfish|Rainfish|Cavefish|Ammonite|Scorpion|Lurker|Murloc)/;
const FISH: DropMeshSpec = { key: "mushroomCommon", height: 0.55, tint: 0x6f9fb0, emissive: 0x101c20 };

// Generic prop per equipment slot — real EXPLORER stone-age tools for gear.
const BY_SLOT: Record<string, DropMeshSpec> = {
  "main-hand":  { source: "prop", key: "axe",      height: 0.85, tint: 0x9a8868 },  // weapon → stone axe
  "offhand":    { source: "prop", key: "shield",   height: 0.75, tint: 0x8a6a44 },  // off-hand → hide shield
  "armor":      { key: "pebbleRound3", height: 0.55, tint: 0x7a5a3a },              // armour → folded hide (no prop)
  "consumable": { source: "prop", key: "foodBowl", height: 0.4,  tint: 0xa07c4c },  // food/potion → bowl
  "utility":    { source: "prop", key: "hammer",   height: 0.65, tint: 0x9a8868 },  // tool/misc → stone hammer
};
const DEFAULT: DropMeshSpec = { key: "exRockS1A", height: 0.6, tint: 0x9a9388 };

/** Resolve a dropped item id to a small 3D ground-prop spec. Never returns null. */
export function dropMeshSpec(itemId: string): DropMeshSpec {
  const explicit = MATERIAL[itemId];
  if (explicit) return explicit;
  if (MEAT_RE.test(itemId)) return MEAT;
  if (FISH_RE.test(itemId)) return FISH;
  const slot = ITEMS[itemId]?.slot;
  return (slot && BY_SLOT[slot]) || DEFAULT;
}
